import { useMemo } from "react";

import PropTypes from "prop-types";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon"; 
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import configs from "examples/Charts/BarCharts/ReportsBarChart/configs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function ReportsBarChart({ color, title, description, chart }) { 
  const theme = useTheme();

  const getChartBackground = (colorName) => {
    const mainColor = theme.palette[colorName]?.main || theme.palette.info.main;
    const darkerColor = theme.palette[colorName]?.dark || theme.palette.info.dark;
    return `linear-gradient(195deg, ${mainColor} 0%, ${darkerColor} 100%)`;
  };

  const { data, options } = useMemo(() => configs(chart.labels || [], chart.datasets || {}), [chart]);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: theme.borders.borderRadius.xl,
        boxShadow: theme.shadows[4],
        transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[6],
        },
        overflow: "visible",
      }}
    >
      <MDBox padding="1rem 1.5rem 0 1.5rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              borderRadius="lg"
              py={2}
              pr={0.5}
              mt={-5}
              height="12.5rem"
              sx={{
                background: getChartBackground(color),
                boxShadow: theme.shadows.lg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                animation: `chartFadeIn 0.8s ease-out`,
                "@keyframes chartFadeIn": {
                  "0%": { opacity: 0, transform: "scale(0.95) translateY(10px)" },
                  "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
                },
              }}
            >
              <Bar data={data} options={options} redraw />
            </MDBox>
          ),
          [color, chart, data, options]
        )}
      </MDBox>
      <MDBox pt={3} pb={2} px={2}>
        <MDTypography variant="h5" textTransform="capitalize" fontWeight="bold" mb={0.5}>
          {title}
        </MDTypography>
        <MDTypography component="div" variant="body2" color="text.secondary">
          {description}
        </MDTypography>
        <Divider sx={{ my: 1.5 }} />
        {}
      </MDBox>
    </Card>
  );
}

ReportsBarChart.defaultProps = {
  color: "info",
  description: "",
  
};

ReportsBarChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
};

export default ReportsBarChart;